import { ExternalLink } from 'lucide-react';
import { projects } from '@/data';
import { Section } from '@/components/layout/Section';
import { Badge } from '@/components/ui/Badge';
import { Card } from '@/components/ui/Card';
import { Reveal } from '@/components/ui/Reveal';
import { GithubIcon } from '@/components/ui/BrandIcons';

/**
 * Full-width case study rather than a filterable card grid.
 *
 * There is one project. A grid with a single card looks broken, and filter
 * chips over a single item are meaningless (PLAN.md §4.4). If a second project
 * is added, this is the component to split.
 */
export function ProjectCaseStudy() {
  return (
    <Section id="projects" title="Selected work">
      {projects.map((project) => (
        <article key={project.id} className="space-y-8">
          <Reveal>
            <div className="flex flex-wrap items-baseline justify-between gap-3">
              <h3 className="text-xl font-semibold text-foreground">
                {project.title}
                {project.year ? (
                  <span className="text-base font-normal text-muted-foreground">
                    {/* Explicit space: without it the text layer reads
                        "DevForge AI2026" even though the margin looks right. */}
                    {' · '}
                    {project.year}
                  </span>
                ) : null}
              </h3>
              <div className="flex items-center gap-3 text-sm">
                {project.repoUrl ? (
                  <a
                    href={project.repoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-muted-foreground transition-colors hover:text-foreground"
                  >
                    <GithubIcon className="h-4 w-4" />
                    Source
                  </a>
                ) : null}
                {project.liveUrl ? (
                  <a
                    href={project.liveUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-muted-foreground transition-colors hover:text-foreground"
                  >
                    <ExternalLink className="h-4 w-4" aria-hidden />
                    Live
                  </a>
                ) : null}
              </div>
            </div>

            <p className="mt-1 text-sm font-medium text-primary">{project.subtitle}</p>
            <p className="mt-4 max-w-3xl text-pretty text-foreground/90">{project.blurb}</p>

            <div className="mt-5 flex flex-wrap gap-2">
              {project.tags.map((tag) => (
                <Badge key={tag} variant="accent">
                  {tag}
                </Badge>
              ))}
            </div>
          </Reveal>

          <Reveal>
            <dl className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
              {project.metrics.map((metric) => (
                <div
                  key={metric.label}
                  className="rounded-lg border border-border bg-card px-3 py-2.5 text-center"
                >
                  <dd className="font-mono text-lg font-bold text-foreground">{metric.value}</dd>
                  <dt className="mt-0.5 text-[0.7rem] leading-tight text-muted-foreground">
                    {metric.label}
                  </dt>
                </div>
              ))}
            </dl>
          </Reveal>

          <div className="grid gap-4 sm:grid-cols-2">
            {project.sections.map((section, i) => (
              <Reveal key={section.heading} index={i}>
                <Card className="h-full">
                  <h4 className="font-semibold text-foreground">{section.heading}</h4>
                  <p className="mt-2 text-sm text-pretty text-muted-foreground">{section.body}</p>
                </Card>
              </Reveal>
            ))}
          </div>

          <Reveal>
            <div>
              <h4 className="text-sm font-semibold text-foreground">Stack</h4>
              <div className="mt-2 flex flex-wrap gap-2">
                {project.tech.map((tech) => (
                  <Badge key={tech} variant="outline">
                    {tech}
                  </Badge>
                ))}
              </div>
            </div>
          </Reveal>
        </article>
      ))}
    </Section>
  );
}
